import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const ComingSoon = () => {
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        setIsVisible(true);

        // Load Google Fonts
        const link = document.createElement('link');
        link.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&family=Poppins:wght@300;400;500;600;700;800;900&family=Space+Grotesk:wght@300;400;500;600;700&family=JetBrains+Mono:wght@300;400;500;600;700&display=swap';
        link.rel = 'stylesheet';
        document.head.appendChild(link);

        return () => {
            document.head.removeChild(link);
        };
    }, []);

    const RocketIcon = ({ size = 32, className = "", animated = false }) => (
        <div className={`${className} ${animated ? 'animate-rocket-float' : ''}`}>
            <svg viewBox="0 0 100 100" width={size} height={size}>
                <defs>
                    <linearGradient id="rocketGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#5294cf" />
                        <stop offset="100%" stopColor="#1d9bf0" />
                    </linearGradient>
                </defs>
                <motion.g
                    animate={animated ? { y: [0, -2, 0] } : {}}
                    transition={animated ? { duration: 2, repeat: Infinity, ease: "easeInOut" } : {}}
                >
                    {/* Rocket body */}
                    <path
                        d="M25 80 L50 10 L75 80 L65 70 L35 70 Z"
                        fill="url(#rocketGradient)"
                        className="drop-shadow-sm"
                    />
                    {/* Window */}
                    <circle
                        cx="50"
                        cy="38"
                        r="12"
                        fill="#f5f5dc"
                        stroke="#e0e0e0"
                        strokeWidth="1"
                    />
                    {/* Eyes */}
                    <circle cx="46" cy="36" r="2" fill="#1f2937" />
                    <circle cx="54" cy="36" r="2" fill="#1f2937" />
                    {/* Smile */}
                    <path
                        d="M 44 42 Q 50 46 56 42"
                        stroke="#1f2937"
                        strokeWidth="1.5"
                        fill="none"
                        strokeLinecap="round"
                    />
                    {/* Flames */}
                    <motion.g
                        animate={animated ? { scaleY: [1, 1.3, 1], opacity: [0.8, 1, 0.8] } : {}}
                        transition={animated ? { duration: 0.6, repeat: Infinity } : {}}
                    >
                        <path d="M35 70 L30 85 L40 78 Z" fill="#ff6b6b" opacity="0.9" />
                        <path d="M50 72 L45 88 L55 88 Z" fill="#ffa726" opacity="0.8" />
                        <path d="M65 70 L70 85 L60 78 Z" fill="#ff6b6b" opacity="0.9" />
                    </motion.g>
                </motion.g>
            </svg>
        </div>
    );

    return (
        <div className="min-h-screen flex flex-col bg-twitter-bg font-twitter text-twitter-text">
            {/* Navigation Bar */}
            <nav className="sticky top-0 z-50 bg-twitter-bg/80 backdrop-blur-sm border-b border-twitter-border">
                <div className="max-w-6xl mx-auto px-4">
                    <div className="flex items-center justify-between h-14">
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6 }}
                            className="flex items-center space-x-3"
                        >
                            <RocketIcon size={28} animated={true} />
                            <div>
                <span className="text-xl font-display font-bold bg-gradient-to-r from-moondat-gradient-start to-moondat-gradient-end bg-clip-text text-transparent">
                  moondat
                </span>
                                <span className="text-twitter-text-secondary font-display">.social</span>
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.5, delay: 0.4 }}
                            className="flex items-center space-x-2"
                        >
                            <div className="w-2 h-2 bg-twitter-green rounded-full animate-pulse-gentle" />
                            <span className="text-sm text-twitter-text-secondary font-medium">Coming Soon</span>
                        </motion.div>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <main className="max-w-6xl flex flex-1 mx-auto px-4 py-8 items-center">
                <div className="text-center mt-20">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.3 }}
                        className="mb-8"
                    >
                        <div className="inline-block mb-6">
                            <RocketIcon size={120} className="animate-rocket-launch" animated={true} />
                        </div>

                        <h1 className="text-hero md:text-6xl lg:text-7xl font-hero font-bold mb-4">
              <span className="bg-gradient-to-r from-moondat-gradient-start via-twitter-blue to-moondat-gradient-end bg-clip-text text-transparent">
                Launch tokens
              </span>
                            <br />
                            <span className="text-twitter-text">from your tweets</span>
                        </h1>

                        <p className="text-xl md:text-2xl text-twitter-text-secondary max-w-3xl mx-auto leading-relaxed">
                            The revolutionary platform that transforms your Twitter posts into tradeable tokens.
                            Verify ownership, stake rewards, and join the future of social finance.
                        </p>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.8 }}
                        className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16"
                    >
                        <motion.a
                            href="https://x.com/moondat_social"
                            target="_blank"
                            rel="noopener noreferrer"
                            whileHover={{ scale: 1.02, boxShadow: "0 4px 20px rgba(29, 155, 240, 0.3)" }}
                            whileTap={{ scale: 0.98 }}
                            className="group bg-twitter-blue hover:bg-twitter-blue-hover text-white px-8 py-4 rounded-twitter font-bold text-lg transition-all duration-200 shadow-twitter-button flex items-center space-x-3 min-w-48"
                        >
                            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                            </svg>
                            <span>Follow on X</span>

                        </motion.a>
                    </motion.div>
                </div>

                {/* Tweet Timeline Section */}
                {/*<motion.div*/}
                {/*    initial={{ opacity: 0, y: 30 }}*/}
                {/*    animate={{ opacity: 1, y: 0 }}*/}
                {/*    transition={{ duration: 0.8, delay: 1 }}*/}
                {/*    className="max-w-2xl mx-auto mb-16"*/}
                {/*>*/}
                {/*    <div className="bg-twitter-bg border border-twitter-border rounded-twitter-xl overflow-hidden shadow-twitter-card">*/}
                {/*        /!* Timeline Header *!/*/}
                {/*        /!*<div className="border-b border-twitter-border p-4 bg-twitter-bg-secondary/50">*!/*/}
                {/*        /!*    <div className="flex items-center justify-between">*!/*/}
                {/*        /!*        <h3 className="text-xl font-display font-bold text-twitter-text">How it works</h3>*!/*/}
                {/*        /!*        <div className="flex space-x-2">*!/*/}
                {/*        /!*            <div className="w-3 h-3 rounded-full bg-twitter-red/20" />*!/*/}
                {/*        /!*            <div className="w-3 h-3 rounded-full bg-twitter-blue/20" />*!/*/}
                {/*        /!*            <div className="w-3 h-3 rounded-full bg-twitter-green/20" />*!/*/}
                {/*        /!*        </div>*!/*/}
                {/*        /!*    </div>*!/*/}
                {/*        /!*</div>*!/*/}

                {/*        /!* Tweet Cards *!/*/}
                {/*        <div className="divide-y divide-twitter-border">*/}
                {/*            {[*/}
                {/*                {*/}
                {/*                    icon: "🚀",*/}
                {/*                    title: "Instant Launch",*/}
                {/*                    username: "moondat_launch",*/}
                {/*                    time: "now",*/}
                {/*                    content: "Launch tokens directly from your tweets with automatic liquidity pool creation on Base network. No coding required!",*/}
                {/*                    engagement: { replies: 42, retweets: 128, likes: 834 },*/}
                {/*                    color: "twitter-blue"*/}
                {/*                },*/}
                {/*                {*/}
                {/*                    icon: "✅",*/}
                {/*                    title: "Verify Ownership",*/}
                {/*                    username: "moondat_verify",*/}
                {/*                    time: "2m",*/}
                {/*                    content: "Verify tweet ownership and claim your 5% creator rewards with vesting schedule. Only real owners can claim!",*/}
                {/*                    engagement: { replies: 67, retweets: 289, likes: 1200 },*/}
                {/*                    color: "twitter-green"*/}
                {/*                },*/}
                {/*                {*/}
                {/*                    icon: "🌙",*/}
                {/*                    title: "$MOON Staking",*/}
                {/*                    username: "moondat_stake",*/}
                {/*                    time: "5m",*/}
                {/*                    content: "Stake $MOON tokens to earn 20% of all trading fees plus get airdrops of every new token launched on the platform!",*/}
                {/*                    engagement: { replies: 156, retweets: 432, likes: 2800 },*/}
                {/*                    color: "moondat-primary"*/}
                {/*                }*/}
                {/*            ].map((tweet, index) => (*/}
                {/*                <motion.div*/}
                {/*                    key={index}*/}
                {/*                    initial={{ opacity: 0, x: -20 }}*/}
                {/*                    animate={{ opacity: 1, x: 0 }}*/}
                {/*                    transition={{ duration: 0.6, delay: 1.2 + index * 0.2 }}*/}
                {/*                    className="p-4 hover:bg-twitter-bg-hover transition-colors duration-200 cursor-pointer"*/}
                {/*                >*/}
                {/*                    <div className="flex space-x-3">*/}
                {/*                        <div className={`w-12 h-12 rounded-full bg-gradient-to-br from-${tweet.color} to-${tweet.color} flex items-center justify-center text-white text-xl font-bold flex-shrink-0`}>*/}
                {/*                            {tweet.icon}*/}
                {/*                        </div>*/}
                {/*                        <div className="flex-1 min-w-0">*/}
                {/*                            <div className="flex items-center space-x-2 mb-1">*/}
                {/*                                <span className="font-bold text-twitter-text">{tweet.title}</span>*/}
                {/*                                <span className="text-twitter-text-secondary">@{tweet.username}</span>*/}
                {/*                                <span className="text-twitter-text-secondary">·</span>*/}
                {/*                                <span className="text-twitter-text-secondary text-sm">{tweet.time}</span>*/}
                {/*                            </div>*/}

                {/*                            <p className="text-twitter-base text-twitter-text leading-relaxed mb-3">*/}
                {/*                                {tweet.content}*/}
                {/*                            </p>*/}

                {/*                            <div className="flex items-center justify-between max-w-md text-twitter-text-secondary text-sm">*/}
                {/*                                <motion.button*/}
                {/*                                    whileHover={{ scale: 1.1 }}*/}
                {/*                                    whileTap={{ scale: 0.9 }}*/}
                {/*                                    className="flex items-center space-x-2 hover:text-twitter-blue transition-colors duration-200 group"*/}
                {/*                                >*/}
                {/*                                    <div className="p-2 rounded-full group-hover:bg-twitter-blue-light transition-colors duration-200">*/}
                {/*                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">*/}
                {/*                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />*/}
                {/*                                        </svg>*/}
                {/*                                    </div>*/}
                {/*                                    <span>{tweet.engagement.replies}</span>*/}
                {/*                                </motion.button>*/}

                {/*                                <motion.button*/}
                {/*                                    whileHover={{ scale: 1.1 }}*/}
                {/*                                    whileTap={{ scale: 0.9 }}*/}
                {/*                                    className="flex items-center space-x-2 hover:text-twitter-green transition-colors duration-200 group"*/}
                {/*                                >*/}
                {/*                                    <div className="p-2 rounded-full group-hover:bg-twitter-green-light transition-colors duration-200">*/}
                {/*                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">*/}
                {/*                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />*/}
                {/*                                        </svg>*/}
                {/*                                    </div>*/}
                {/*                                    <span>{tweet.engagement.retweets}</span>*/}
                {/*                                </motion.button>*/}

                {/*                                <motion.button*/}
                {/*                                    whileHover={{ scale: 1.1 }}*/}
                {/*                                    whileTap={{ scale: 0.9 }}*/}
                {/*                                    className="flex items-center space-x-2 hover:text-twitter-red transition-colors duration-200 group"*/}
                {/*                                >*/}
                {/*                                    <div className="p-2 rounded-full group-hover:bg-twitter-red-light transition-colors duration-200">*/}
                {/*                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">*/}
                {/*                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />*/}
                {/*                                        </svg>*/}
                {/*                                    </div>*/}
                {/*                                    <span>{tweet.engagement.likes.toLocaleString()}</span>*/}
                {/*                                </motion.button>*/}
                {/*                            </div>*/}
                {/*                        </div>*/}
                {/*                    </div>*/}
                {/*                </motion.div>*/}
                {/*            ))}*/}
                {/*        </div>*/}
                {/*    </div>*/}
                {/*</motion.div>*/}

                {/* Stats Section */}
                {/*<motion.div*/}
                {/*    initial={{ opacity: 0, y: 30 }}*/}
                {/*    animate={{ opacity: 1, y: 0 }}*/}
                {/*    transition={{ duration: 0.8, delay: 1.5 }}*/}
                {/*    className="mb-16"*/}
                {/*>*/}
                {/*    <div className="bg-gradient-to-r from-twitter-blue-bg to-twitter-bg-secondary rounded-twitter-2xl p-8 border border-twitter-border shadow-twitter-lg">*/}
                {/*        <div className="text-center mb-8">*/}
                {/*            <h3 className="text-2xl font-display font-bold text-twitter-text mb-2">Built on Base • Powered by Uniswap</h3>*/}
                {/*            <p className="text-twitter-text-secondary font-twitter">Fair, transparent, and decentralized token launches</p>*/}
                {/*        </div>*/}

                {/*        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">*/}
                {/*            {[*/}
                {/*                { value: "1B", label: "Total Supply", sublabel: "per Token", color: "twitter-blue" },*/}
                {/*                { value: "5%", label: "Creator", sublabel: "Rewards", color: "twitter-green" },*/}
                {/*                { value: "20%", label: "$MOON Staker", sublabel: "Fee Share", color: "moondat-primary" },*/}
                {/*                { value: "0%", label: "Platform", sublabel: "Pre-mine", color: "twitter-red" }*/}
                {/*            ].map((stat, index) => (*/}
                {/*                <motion.div*/}
                {/*                    key={index}*/}
                {/*                    initial={{ opacity: 0, y: 20 }}*/}
                {/*                    animate={{ opacity: 1, y: 0 }}*/}
                {/*                    transition={{ duration: 0.6, delay: 1.7 + index * 0.1 }}*/}
                {/*                    className="text-center p-6 bg-twitter-bg rounded-twitter-lg border border-twitter-border hover:shadow-twitter-hover transition-all duration-300"*/}
                {/*                >*/}
                {/*                    <div className={`text-4xl font-bold mb-2 text-${stat.color}`}>*/}
                {/*                        {stat.value}*/}
                {/*                    </div>*/}
                {/*                    <div className="text-twitter-text font-semibold">*/}
                {/*                        {stat.label}*/}
                {/*                    </div>*/}
                {/*                    <div className="text-twitter-text-secondary text-sm">*/}
                {/*                        {stat.sublabel}*/}
                {/*                    </div>*/}
                {/*                </motion.div>*/}
                {/*            ))}*/}
                {/*        </div>*/}
                {/*    </div>*/}
                {/*</motion.div>*/}

                {/* Final CTA */}
                {/*<motion.div*/}
                {/*    initial={{ opacity: 0, y: 30 }}*/}
                {/*    animate={{ opacity: 1, y: 0 }}*/}
                {/*    transition={{ duration: 0.8, delay: 2 }}*/}
                {/*    className="text-center"*/}
                {/*>*/}
                {/*    <div className="bg-twitter-bg border border-twitter-border rounded-twitter-2xl p-8 shadow-twitter-lg">*/}
                {/*        <RocketIcon size={60} className="mx-auto mb-4" animated={true} />*/}
                {/*        <h3 className="text-2xl font-display font-bold text-twitter-text mb-4">*/}
                {/*            Ready for Launch? 🚀*/}
                {/*        </h3>*/}
                {/*        <p className="text-twitter-text-secondary mb-6 max-w-md mx-auto">*/}
                {/*            Be the first to know when moondat.social goes live.*/}
                {/*            Join our community and get early access!*/}
                {/*        </p>*/}
                {/*        <div className="flex flex-col sm:flex-row gap-3 justify-center">*/}
                {/*            <motion.a*/}
                {/*                href="https://x.com/moondat_social"*/}
                {/*                target="_blank"*/}
                {/*                rel="noopener noreferrer"*/}
                {/*                whileHover={{ scale: 1.05 }}*/}
                {/*                whileTap={{ scale: 0.95 }}*/}
                {/*                className="bg-twitter-blue hover:bg-twitter-blue-hover text-white px-6 py-3 rounded-twitter font-semibold transition-all duration-200 flex items-center justify-center space-x-2"*/}
                {/*            >*/}
                {/*                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">*/}
                {/*                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>*/}
                {/*                </svg>*/}
                {/*                <span>Get Updates</span>*/}
                {/*            </motion.a>*/}

                {/*        </div>*/}
                {/*    </div>*/}
                {/*</motion.div>*/}
            </main>

            {/* Footer */}
            <footer className="border-t border-twitter-border bg-twitter-bg-secondary mt-16">
                <div className="max-w-6xl mx-auto px-4 py-8">
                    <div className="flex flex-col md:flex-row items-center justify-between">
                        <div className="flex items-center space-x-3 mb-4 md:mb-0">
                            <RocketIcon size={24} />
                            <span className="text-twitter-text-secondary">
                © 2025 Moondat.social • Built with ❤️ for the community
              </span>
                        </div>
                        <div className="flex items-center space-x-4 text-twitter-text-secondary">
                            <span className="text-sm">Powered by</span>
                            <div className="flex items-center space-x-2">
                                <div className="w-4 h-4 bg-blue-600 rounded-sm" />
                                <span className="text-sm font-medium">Base</span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <div className="w-4 h-4 bg-pink-500 rounded-sm" />
                                <span className="text-sm font-medium">Uniswap</span>
                            </div>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default ComingSoon;